import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /**
   * MSH 2026-10-01: in-flight product-page requests, keyed by URL, so a hover
   * prefetch and a click on the same card share ONE fetch instead of racing.
   * @type {Map<string, Promise<Element | null>>}
   */
  #pendingContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });

    /* MSH 2026-10-01 (May): "when they click the add to cart icon it should
       automatically show the pop up for choices. For now it takes several
       seconds."

       The chooser cannot open until the WHOLE product page has been fetched
       and parsed - handleClick below awaits it before showDialog(). On this
       store a product page carries 16 to 50 photos, so that is seconds on a
       first click, and instant on a second one only because the result is
       cached per card.

       So start the same fetch when the shopper's pointer reaches the card,
       which is a few hundred milliseconds of head start at worst and usually
       much more. By the time the button is clicked the page is normally in
       #cachedContent and the modal opens with no wait at all. Nothing else
       changes: a click that arrives before the prefetch finishes simply waits
       on the SAME request (see #loadProductGrid) rather than starting a
       second one, and a cold click behaves exactly as it did before.

       Deliberately NOT a blind prefetch of every card on the page: these
       documents are large, and May wants the storefront light. One hover,
       one fetch, once per card. */
    warmTopOfGrid();

    const prefetchTarget = this.closest('product-card') ?? this;
    prefetchTarget.addEventListener('pointerenter', this.prefetchProductPage, {
      signal: this.#cartUpdateAbortController.signal,
    });
    prefetchTarget.addEventListener('focusin', this.prefetchProductPage, {
      signal: this.#cartUpdateAbortController.signal,
    });
    prefetchTarget.addEventListener('touchstart', this.prefetchProductPage, {
      passive: true,
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    // Only flip choose <-> add when both buttons were rendered.
    // Otherwise the flip would hide the sole rendered button and reveal nothing.
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * MSH 2026-10-01: one place that turns a product URL into the modal's
   * content, whether the caller is a hover or a click.
   *
   * Three states, in order: already cached (returns immediately, no network),
   * already being fetched (returns that same promise - this is what stops a
   * click from abandoning the hover's half-finished request and starting
   * again), or not started yet.
   *
   * Does not use fetchProductPage(), which aborts whatever was in flight -
   * exactly the wrong behaviour when the thing in flight is the prefetch we
   * want to wait for. Failures resolve to null and the caller falls back to
   * opening an empty dialog, as it did before.
   *
   * @param {string} url
   * @returns {Promise<Element | null>}
   */
  #loadProductGrid(url) {
    if (!url) return Promise.resolve(null);

    const cached = this.#cachedContent.get(url);
    if (cached) return Promise.resolve(cached);

    const pending = this.#pendingContent.get(url);
    if (pending) return pending;

    const request = fetch(url, { priority: 'low' })
      .then((response) => (response.ok ? response.text() : null))
      .then((responseText) => {
        if (!responseText) return null;

        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (!gridElement) return null;

        // Cache the clone, so the copy handed out is never the one mutated.
        const productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
        this.#cachedContent.set(url, productGrid);
        return productGrid;
      })
      .catch(() => null)
      .finally(() => this.#pendingContent.delete(url));

    this.#pendingContent.set(url, request);
    return request;
  }

  /** MSH 2026-10-01: warm the chooser as the pointer arrives on the card. */
  prefetchProductPage = () => {
    if (this.dataset.usesSellingPlans === 'true') return;
    this.#loadProductGrid(this.productPageUrl);
  };

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    if (this.dataset.usesSellingPlans === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    // MSH 2026-10-01: cached, in flight from the hover prefetch, or fetched now.
    const productGrid = await this.#loadProductGrid(currentUrl);

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }

      /* MSH (2026-09-11): rescue the two custom pieces that live INSIDE
         .product-details before the line below deletes that wrapper.

         This mobile branch keeps only variant-picker + product-form-component
         and throws the rest of the product details column away. That is fine
         for stock Horizon, but on this store two things the customer is
         expected to see in the buy box are casualties of it:

         1. blocks/msh-buybox-style.liquid renders a settings-driven
            `{% style %}` tag (the merchant-editable Men=blue / Women=pink
            --msh-accent mapping and the Sole Color rules). A `{% style %}`
            tag is emitted INLINE where the block sits, i.e. inside
            .product-details, so removing that wrapper removed the only copy
            of those rules. The collection page this modal opens from never
            renders that block itself, so there is no other copy anywhere.
            Moving the <style> node up keeps it in the subtree that gets
            morphed into the modal, which is what makes the selected Style
            pill and the selected Size button pink/blue instead of the theme
            default. (Its static, settings-independent half lives in a
            `{% stylesheet %}` bundle that is already loaded on every page.)

         2. blocks/msh-size-guide.liquid - the pink/blue Size Guide trigger
            and its chart modal. Without this it is simply absent from the
            mobile quick-add modal, so a shopper picking a size from a
            collection card has no chart to check it against.

         Both are moved rather than cloned: productGrid here is already a
         throwaway clone of the cached copy (see handleClick), so nothing on
         the real page is touched. The size guide is appended after the
         variant picker and before the buy buttons, matching its position on
         the real product page (templates/product.json block order). */
      for (const mshStyle of productGrid.querySelectorAll('style')) {
        productGrid.appendChild(mshStyle);
      }

      const mshSizeGuide = productGrid.querySelector('.msh-size-guide');
      if (mshSizeGuide) {
        productGrid.appendChild(mshSizeGuide);
      }

      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

/**
 * MSH 2026-10-01: a phone has no hover, so the pointerenter prefetch above can
 * only ever buy the ~100ms between touchstart and click there. Warm the first
 * few cards once the page is otherwise idle instead, which is where a shopper
 * taps first.
 *
 * Deliberately conservative: FOUR cards, once per page load, after load and at
 * idle priority, and skipped entirely on a metered or slow connection. Each
 * product page is ~60KB over the wire, so this is a bounded cost, not a
 * prefetch-everything. Scrolling further still relies on hover/touch.
 */
let topOfGridWarmed = false;

function warmTopOfGrid() {
  if (topOfGridWarmed) return;
  topOfGridWarmed = true;

  const connection = /** @type {any} */ (navigator).connection;
  if (connection?.saveData) return;
  if (connection?.effectiveType && /2g/.test(connection.effectiveType)) return;

  const run = () => {
    let warmed = 0;
    for (const card of document.querySelectorAll('quick-add-component')) {
      if (warmed >= 4) break;
      if (!(card instanceof QuickAddComponent)) continue;
      card.prefetchProductPage();
      warmed++;
    }
  };

  const idle = /** @type {any} */ (window).requestIdleCallback ?? ((fn) => setTimeout(fn, 1200));
  const start = () => idle(run, { timeout: 4000 });

  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
